import React, { useEffect, useState } from 'react'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { Col, message, Row, Space } from 'antd'
import { ActionButton, PrimaryButton } from 'Components/button'
import { formatDateTime } from 'helpers/format'
import {
  DetailReq,
  ProductOptions,
  UpdateProductOptionPrice,
  UpdateProductPriceReq,
} from 'Store/product/types'
import { getProductDetailAPI, updateProductPriceAPI } from 'Store/product/api'
import { selectActionEvent, selectDetail, selectDetailEvent } from 'Store/product/selector'
import { selectCurrentUser } from 'Store/user/selector'
import EventModal from 'Components/modal/EventModal'
import { clearEventAction } from 'Store/product/actions'
import LoadingView from 'Components/modal/LoadingView'
import { DetailDrawer } from 'Components/common/MainLayout'
import styled from 'styled-components'
import { MyTheme } from 'Components/common/Theme'
import RequestHistoryDrawer from './components/RequestHistoryDrawer'
import { ConfirmQuestion } from 'Components/modal'
import { selectDatas as selectDeliveryDatas } from 'Store/delivery-cost/selector'
import { selectIsPackageC } from 'Store/pos/selector'
import { LatestGrnListReq, LatestGrnReq } from 'Store/grn/types'
import { getLatestGrnListAPI } from 'Store/grn/api'
import { getUserLogin } from 'helpers/account'
import { FileDoneOutlined } from '@ant-design/icons'
import { appConfig, themeConfig } from 'config'
import ProductOptionsEditPrice from './components/ProductOptionsEditPrice'
import { cloneDeep } from 'lodash'

type Props = ReduxProps & {
  visible: boolean
  onCloseDrawer: () => void
  onSuccess?: () => void
  mode: any
  id?: number
}

const AdjustPrice: React.FunctionComponent<Props> = props => {
  const {
    onCloseDrawer,
    visible,
    currentUser,
    event,
    onSuccess = () => {},
    id = 0,
    mode,
    detail,
    detailEvent,
    isPackageC,
  } = props
  const isCreateMode = mode === 'create'
  const userInfo = getUserLogin()

  const [confirmRequestDialog, setConfirmRequestDialog] = useState(false)
  const [showRequestedHistories, setShowRequestedHistories] = useState(false)
  const [data, setData] = useState([] as ProductOptions[])
  const isSupperAdmin = currentUser?.activeMerchant?.id === parseInt(appConfig.merchantID || '')
  const [editingKey, setEditingKey] = useState('')
  const dispatch = useDispatch()
  const enableProductOnline = themeConfig?.component?.elementOnlineProduct

  useEffect(() => {
    if (visible && !isCreateMode && id > 0) {
      onGetDetail()
      setEditingKey('')
    }
  }, [visible])

  useEffect(() => {
    if (detail && detail.product_options) {
      const originData: ProductOptions[] = []
      for (let i = 0; i < detail.product_options.length; i++) {
        originData.push({
          ...detail.product_options[i],
          key: i.toString(),
        })
      }
      setData([...originData])
    }
  }, [detail])

  const onGetDetail = (): void => {
    const req: DetailReq = {
      id,
      is_admin: true,
    }
    dispatch(getProductDetailAPI(req, currentUser.accessToken))
  }

  const onGetLatestGrnList = (productsReq: LatestGrnReq[]): void => {
    const req: LatestGrnListReq = {
      products: productsReq,
    }
    dispatch(getLatestGrnListAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  useEffect(() => {
    if (detail && detail.product_options && isPackageC && visible) {
      const productsReq: LatestGrnReq[] = []
      detail.product_options &&
        detail.product_options.forEach(item => {
          productsReq.push({
            product_id: id,
            product_option_id: item?.id,
          })
        })
      onGetLatestGrnList(productsReq)
    }
  }, [detail])

  const formatUpdatePriceReq = () => {
    const req = [] as UpdateProductOptionPrice[]
    data &&
      data.forEach(item => {
        const option = {
          id: item.id || 0,
          price: item.price,
        } as UpdateProductOptionPrice

        if (isPackageC) {
          option.price_a = item.price_a || 0
          option.price_b = item.price_b || 0
          option.price_c = item.price_c || 0
          option.offline_price = item.offline_price || 0
          if (!enableProductOnline) {
            option.price = item.offline_price || 0
          }
        }
        req.push(option)
      })
    return req
  }

  const onUpdate = (update_remark: string = ''): void => {
    const req: UpdateProductPriceReq = {
      id,
      update_remark,
      product_options: formatUpdatePriceReq(),
      request_action_by: userInfo?.username || '',
    }
    dispatch(updateProductPriceAPI(req, currentUser.accessToken))
  }

  const validatePrice = () => {
    if (isPackageC) {
      const foundError = data && data.find(item => !item.price || !item.offline_price)
      if (foundError) {
        return false
      }
    } else {
      const foundError = data && data.find(item => !item.price)
      if (foundError) {
        return false
      }
    }
    return true
  }

  const onClickUpdate = () => {
    if (!validatePrice()) {
      message.error('กรุณาระบุราคาให้ถูกต้อง')
      return
    }
    onOpenConfirmRequestUpdate()
  }

  const onUpdateData = (data: ProductOptions[]) => {
    setData(data)
  }

  const clearEvent = (): void => {
    dispatch(clearEventAction())
  }

  const onEventSuccess = (): void => {
    clearEvent()
    onSuccess()
    onCloseConfirmRequestUpdate()
  }

  const onCloseConfirmRequestUpdate = () => {
    setConfirmRequestDialog(false)
  }

  const onOpenConfirmRequestUpdate = () => {
    setConfirmRequestDialog(true)
  }

  const FooterContent = (
    <Space size="middle" align="baseline">
      <PrimaryButton text="ยกเลิก" btnType="Cancel" onClick={onCloseDrawer} />
      <PrimaryButton text="ยืนยันการแก้ไขราคา" onClick={onClickUpdate} disabled={editingKey !== ''} />
    </Space>
  )

  const title = (
    <>
      <Row justify="start" gutter={16} align="middle">
        <Col>จัดการราคาสินค้า : {detail?.title}</Col>
        {!isCreateMode && !isSupperAdmin && (
          <Col>
            <ActionButton
              text=""
              icon={<FileDoneOutlined />}
              label="ประวัติการแก้ไข"
              action="edit"
              onClick={() => setShowRequestedHistories(true)}
            />
          </Col>
        )}
      </Row>
      {!isCreateMode && (
        <Row>
          <Col>
            <Description>รหัสสินค้า {detail?.id}</Description> &nbsp;
            <Description>วันที่สร้าง {formatDateTime(detail?.created_at || '')}</Description>
          </Col>
        </Row>
      )}
    </>
  )

  const requestHistory = (
    <RequestHistoryDrawer
      isShow={showRequestedHistories}
      onClose={() => setShowRequestedHistories(false)}
      productID={detail?.id || 0}
    />
  )

  const ConfirmRequestUpdate = (
    <ConfirmQuestion
      title="ยืนยันแก้ไขราคา"
      description="หมายเหตุ : ข้อมูลสินค้าของคุณจะถูกเปลี่ยนแปลงทันทีหลังกดยืนยัน"
      label="กรุณาระบุรายละเอียดการแก้ไข"
      placeholder="เช่น แก้ไขราคาสินค้าขายออนไลน์..."
      confirmBtnTxt="ยืนยัน"
      isShow={confirmRequestDialog}
      handleOk={(remark: string) => onUpdate(remark)}
      handleCancel={onCloseConfirmRequestUpdate}
      loading={event.IsLoading}
    />
  )

  return (
    <DetailDrawer
      title={title}
      onCloseDrawer={onCloseDrawer}
      visible={visible}
      footer={FooterContent}
      width={'85%'}
    >
      <LoadingView isShow={detailEvent.IsLoading} />
      <EventModal
        event={[event]}
        enable={visible}
        successCalback={onEventSuccess}
        errorCalback={clearEvent}
      />
      <Row justify="space-between" gutter={16}>
        {/* <Col md={24} xs={24}>
          <Title>รายละเอียดสินค้า</Title>
          <MultiUploadForm
            label="รูปภาพสินค้า"
            token={''}
            onUpdateData={() => {}}
            files={detail?.photos || []}
            listType="picture-card"
            name="photos"
            readOnly={true}
          />
        </Col>
        <Col md={12} xs={24}>
          {renderInfo('ชื่อสินค้า', detail?.title)}
          {renderInfo('รหัสสินค้า', detail?.code)}
          {renderInfo('หมวดหมู่สินค้า', getCategoriesName(detail))}
          {renderInfo('รุ่น', detail?.model)}
          {renderInfo('หน่วย', detail?.uom?.name)}
          {renderInfo('รายละเอียดสินค้า', detail?.details)}
          {renderInfo('วิธีการใช้งาน', detail?.manual)}
          {renderInfo('การบำรุงรักษา', detail?.maintenant_and_caution)}
          {renderInfo('คุณสมบัติสินค้า', detail?.properties)}
        </Col>
        <Col md={12} xs={24}>
          <Title>ตั้งค่าสินค้า</Title>
          {renderInfo('การรับประกัน', waranty)}
          {renderInfo('รับคืนสินค้า', refund)}
          {renderInfo('ช่องทางการจัดส่ง', getShippingTypes(detail?.shipping_types || []))}
          {enableProductOnline && renderInfo('สถานะสินค้าบนออนไลน์', renderProductStatus(detail?.status))}
          {renderInfo('สถานะสินค้าบน Terminal ', renderProductStatus(detail?.status_pos))}
        </Col> */}
        {/* <Col md={24} xs={24}>
          <ProductOptionsEditable
            onUpdateData={onUpdateData}
            isPackageC={isPackageC}
            data={data}
            productID={id}
            editingKey={editingKey}
            setEditingKey={setEditingKey}
          />
        </Col> */}

        {data.map((item, index) => {
          return (
            <Col span={24} key={index}>
              <ProductOptionsEditPrice
                onUpdate={(result: ProductOptions) => {
                  const newdata = cloneDeep(data) as ProductOptions[]
                  newdata[index] = result
                  onUpdateData(newdata)
                }}
                isPackageC={isPackageC}
                data={item}
                productID={id}
                key={index}
              />
            </Col>
          )
        })}
      </Row>
      {!isCreateMode && requestHistory}
      {ConfirmRequestUpdate}
    </DetailDrawer>
  )
}

const Description = styled.span`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body4};
  color: ${(props: { theme: MyTheme }): string => props.theme.colors.grey};
`

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    event: selectActionEvent(state),
    detail: selectDetail(state),
    detailEvent: selectDetailEvent(state),
    deliveryCost: selectDeliveryDatas(state),
    isPackageC: selectIsPackageC(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

export default connect(mapStateToProps)(AdjustPrice)
